// Where things sit on the bench, in millimetres of SVG space.

import type { Endpoint, Layout } from "./types";

export const SCENE = { width: 244, height: 162 };
// The breadboard and the microcontroller board are drawn at WORK_SCALE times
// their real millimetres so the circuit is readable; the instruments stay 1:1.
export const WORK_SCALE = 1.5;

export const SUPPLY = {
  x: 4, y: 4, w: 100, h: 60,
  lcd: { x: 8, y: 15, w: 62, h: 22 },
  // Front-panel knobs: value runs over a 270° sweep; dragging up and down
  // turns them (perMm = units per millimetre of SVG space).
  knobs: {
    volt: { cx: 18, cy: 48, r: 6, max: 30, step: 0.1, perMm: 0.5, label: "VOLTAGE", field: "setVoltage" },
    lim: { cx: 44, cy: 48, r: 6, max: 3, step: 0.01, perMm: 0.05, label: "CURRENT", field: "currentLimit" },
  },
  output: { x: 62, y: 41, w: 16, h: 12 },
  jacks: { pos: { x: 84, y: 52 }, neg: { x: 96, y: 52 } },
};
export type KnobName = keyof typeof SUPPLY.knobs;
export function knobAngle(name: KnobName, value: number): number {
  return -135 + 270 * Math.min(1, Math.max(0, value / SUPPLY.knobs[name].max));
}
export const BATTERY = { x: 214, y: 8, w: 26, h: 46, jacks: { pos: { x: 221, y: 12 }, neg: { x: 233, y: 12 } } };
// The meter lies on its side along the top so the breadboard gets the width.
export const DMM = {
  x: 108, y: 4, w: 100, h: 60,
  display: { x: 112, y: 10, w: 46, h: 16 },
  dial: { cx: 176, cy: 30, r: 9 },
  jacks: { COM: { x: 118, y: 55 }, V: { x: 134, y: 55 }, mA: { x: 150, y: 55 }, "10A": { x: 166, y: 55 } },
  park: { red: { x: 197, y: 45 }, black: { x: 197, y: 57 } },
};
export const BREADBOARD = { x: 4, y: 72 };
export const BOARD = { x: 136, y: 72 };

export interface Point { x: number; y: number }

export class Geometry {
  private holes = new Map<string, Point>();
  private holeList: { id: string; p: Point }[] = [];
  private pins = new Map<string, Point>();

  constructor(public readonly layout: Layout) {
    for (const h of layout.breadboard.holes) {
      const p = { x: BREADBOARD.x + h.x * WORK_SCALE, y: BREADBOARD.y + h.y * WORK_SCALE };
      this.holes.set(h.id, p);
      this.holeList.push({ id: h.id, p });
    }
    for (const pin of layout.board.pins) {
      this.pins.set(pin.name, { x: BOARD.x + pin.x * WORK_SCALE, y: BOARD.y + pin.y * WORK_SCALE });
    }
  }

  hole(id: string): Point | undefined {
    return this.holes.get(id);
  }

  terminal(instrument: string, terminal: string): Point | undefined {
    if (instrument === "psu") return (SUPPLY.jacks as Record<string, Point>)[terminal];
    if (instrument === "bat") return (BATTERY.jacks as Record<string, Point>)[terminal];
    if (instrument === "board") return this.pins.get(terminal);
    return undefined;
  }

  endpoint(e: Endpoint): Point | undefined {
    return e.kind === "hole" ? this.hole(e.hole) : this.terminal(e.instrument, e.terminal);
  }

  /** Nearest hole or instrument terminal within `radius` mm. */
  nearest(p: Point, radius: number, stripOnly = false): Endpoint | null {
    let best: Endpoint | null = null;
    let bestD = radius * radius;
    for (const h of this.holeList) {
      if (stripOnly && !/^[a-j]\d+$/.test(h.id)) continue;
      const d = dist2(p, h.p);
      if (d < bestD) { bestD = d; best = { kind: "hole", hole: h.id }; }
    }
    if (stripOnly) return best;
    const check = (instrument: string, terminal: string, q: Point) => {
      const d = dist2(p, q);
      if (d < bestD) { bestD = d; best = { kind: "terminal", instrument, terminal }; }
    };
    check("psu", "pos", SUPPLY.jacks.pos);
    check("psu", "neg", SUPPLY.jacks.neg);
    check("bat", "pos", BATTERY.jacks.pos);
    check("bat", "neg", BATTERY.jacks.neg);
    for (const [name, q] of this.pins) check("board", name, q);
    return best;
  }
}

function dist2(a: Point, b: Point): number {
  const dx = a.x - b.x, dy = a.y - b.y;
  return dx * dx + dy * dy;
}

/** Hole `span` columns to the right of a strip hole, same row. */
export function holeRightOf(id: string, span: number): string | null {
  const m = /^([a-j])(\d+)$/.exec(id);
  if (!m) return null;
  const col = Number(m[2]) + span;
  if (col < 1 || col > 30) return null;
  return `${m[1]}${col}`;
}

export function endpointKey(e: Endpoint): string {
  return e.kind === "hole" ? `hole:${e.hole}` : `${e.instrument}:${e.terminal}`;
}

export function endpointLabel(e: Endpoint): string {
  if (e.kind === "hole") return `hole ${e.hole}`;
  const names: Record<string, string> = { psu: "supply", bat: "battery", board: "board" };
  const term: Record<string, string> = { pos: "+", neg: "−" };
  return `${names[e.instrument] ?? e.instrument} ${term[e.terminal] ?? e.terminal}`;
}

export function sameEndpoint(a: Endpoint | null, b: Endpoint | null): boolean {
  return !!a && !!b && endpointKey(a) === endpointKey(b);
}
